import {
  ApplicationTypeEnumType,
  childCaregiverStatusEnumSchema,
  citizenshipStatusEnumSchema,
  healthInsuranceOrCoverageStatusEnumSchema,
  medicarePlanTypeEnumSchema,
} from "@libs/api/patients";
import { OtherStatusEnum } from "@/domain/applications";
import { AgentAssistedProgramType } from "@/domain/brokerConsent";
import { HouseholdMemberType } from "@/domain/householdMember";
import { i18n, I18nKeyType } from "@/i18n";
import { formatMemberList, getProgramTypeLabel } from "@/utils/brandLabel";

export type ApplicationYesNoAnswerType = "yes" | "no" | "unanswered";

export const getApplicationTypeLabel = (
  applicationType: ApplicationTypeEnumType,
) => {
  switch (applicationType) {
    case "MARKETPLACE":
      return "common:marketplace";
    case "MEDICAID":
      return "common:medicaid";
    case "MEDICARE":
      return "common:medicare";
    default:
      return applicationType;
  }
};

export const getAgentAssistedProgramsLabel = (
  programs: AgentAssistedProgramType[],
): string => formatMemberList({ names: programs.map(getProgramTypeLabel) });

export const getOtherStatusFieldLabel = (
  statusField: OtherStatusEnum,
): I18nKeyType => {
  switch (statusField) {
    case OtherStatusEnum.NEEDS_HELP_DAILY_ACTIVITIES_STATUS:
      return "native:applicationOtherStatusesScreen.conditions.needsHelpDailyActivities";
    case OtherStatusEnum.VETERAN_OR_MILITARY_MEMBER_STATUS:
      return "native:applicationOtherStatusesScreen.conditions.veteranOrMilitaryMember";
    case OtherStatusEnum.AMERICAN_INDIAN_OR_ALASKA_NATIVE_STATUS:
      return "native:applicationOtherStatusesScreen.conditions.americanIndianOrAlaskaNative";
    case OtherStatusEnum.STUDENT_FULL_TIME_18_TO_20_STATUS:
      return "native:applicationOtherStatusesScreen.conditions.studentFullTime18to20";
  }
};

export const getHealthInsuranceOrCoverageStatusOptions = (
  shouldIncludeNoOption = true,
) =>
  healthInsuranceOrCoverageStatusEnumSchema.options
    .filter(
      option =>
        shouldIncludeNoOption ||
        option !== healthInsuranceOrCoverageStatusEnumSchema.enum.NOT_COVERED,
    )
    .map(option => ({
      value: option,
      label: i18n.t(`common:healthInsuranceOrCoverageStatus.${option}`),
    }));

export const getMedicarePlanTypeOptions = () =>
  medicarePlanTypeEnumSchema.options.map(option => ({
    value: option,
    label: i18n.t(`common:medicarePlanType.${option}`),
  }));

/**
 * Derives the Coverage Status Yes/No answer from saved household member data so
 * the screen reflects a previously saved selection when the patient navigates
 * back or resumes the application.
 */
export const deriveCoverageStatusAnswer = (
  householdMembers: HouseholdMemberType[],
): ApplicationYesNoAnswerType => {
  if (householdMembers.length === 0) return "unanswered";

  const isSomeoneCovered = householdMembers.some(member =>
    (member.healthInsuranceOrCoverageStatus ?? []).some(
      status =>
        status !== healthInsuranceOrCoverageStatusEnumSchema.enum.NOT_COVERED,
    ),
  );
  if (isSomeoneCovered) return "yes";

  const isEveryoneNotCovered = householdMembers.every(member =>
    (member.healthInsuranceOrCoverageStatus ?? []).includes(
      healthInsuranceOrCoverageStatusEnumSchema.enum.NOT_COVERED,
    ),
  );
  if (isEveryoneNotCovered) return "no";

  return "unanswered";
};

/**
 * Derives the Application Eligibility (citizenship) Yes/No answer from saved
 * household member data so the screen reflects a previously saved selection when
 * the patient navigates back or resumes the application.
 */
export const deriveCitizenshipStatusAnswer = (
  householdMembers: HouseholdMemberType[],
): ApplicationYesNoAnswerType => {
  if (householdMembers.length === 0) return "unanswered";

  const hasNonCitizenMember = householdMembers.some(
    member =>
      member.citizenshipStatus &&
      member.citizenshipStatus !== citizenshipStatusEnumSchema.enum.US_CITIZEN,
  );
  if (hasNonCitizenMember) return "no";

  const isEveryoneCitizen = householdMembers.every(
    member =>
      member.citizenshipStatus === citizenshipStatusEnumSchema.enum.US_CITIZEN,
  );
  if (isEveryoneCitizen) return "yes";

  return "unanswered";
};

export const getChildCaregiverStatusOptions = () =>
  childCaregiverStatusEnumSchema.options.map(option => ({
    value: option,
    label: i18n.t(`common:childCaregiverStatus.${option}`),
  }));

export const getApplicationTypeColor = (
  applicationType: ApplicationTypeEnumType,
) => {
  switch (applicationType) {
    case "MEDICAID":
      return "bg-chart-one";
    case "MEDICARE":
      return "bg-chart-two";
    case "MARKETPLACE":
      return "bg-chart-three";
    default:
      return "";
  }
};
